import { Fragment, useCallback, useMemo } from 'react'

import { Stack } from '@mui/material'
import {
  AutocompleteFilter,
  DateFilter,
  FreeTextFilter,
  SelectFilter
} from '@/components/table/filters'
import { DateRangeFilter } from '@/components/table/filters/DateRangeFilter/DateRangeFilter'

import { Filter, FilterType, SelectedFilters } from '../FilterableTable.types'
import { SelectedFiltersChips } from './SelectedFiltersChips'

type Props<Data extends Record<string, unknown>> = {
  filters: Filter<Data>[]
  selectedFilters?: SelectedFilters
  onFiltersChange: (filters: SelectedFilters) => void
}

export const FilterToolbar = <Data extends Record<string, unknown>>({
  filters,
  selectedFilters,
  onFiltersChange
}: Props<Data>) => {
  const handleFiltersChange = useCallback(
    (newFilters: SelectedFilters) => {
      const filtersHaveChanged =
        Object.keys(newFilters).length !== Object.keys(selectedFilters || {}).length ||
        Object.entries(newFilters).some(([key, value = []]) => {
          const currentFilterValues = selectedFilters?.[key] || []
          return (
            currentFilterValues.length !== value.length ||
            currentFilterValues.some((currentValue) => {
              return !value.some(
                (newValue) =>
                  newValue.value === currentValue.value && newValue.label === currentValue.label
              )
            })
          )
        })

      if (filtersHaveChanged) {
        onFiltersChange(newFilters)
      }
    },
    [onFiltersChange, selectedFilters]
  )

  const Filters = useMemo(() => {
    const getComponent = (filter: Filter<Data>) => {
      const filterValue = selectedFilters?.[filter.paramName] || []

      switch (filter.type) {
        case 'select':
          return (
            <SelectFilter
              onFilterSelect={(value) =>
                handleFiltersChange({
                  ...selectedFilters,
                  [filter.paramName]: filter.multiple ? [...filterValue, value] : [value]
                })
              }
              label={filter.label}
              options={filter.options}
              selectedOptions={filterValue}
              width={filter.width}
            />
          )
        case 'freeTextSearch':
          return (
            <FreeTextFilter
              onFilterSelect={(value) => {
                if (value) {
                  return handleFiltersChange({ ...selectedFilters, [filter.paramName]: [value] })
                }
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { [filter.paramName]: _, ...rest } = selectedFilters || {}
                handleFiltersChange(rest)
              }}
              width={filter.width}
              debounceDelay={filter.debounceDelay}
              defaultValue={filterValue[0]?.value ? `${filterValue[0].value}` : ''}
              placeholder={filter.placeholder}
            />
          )
        case 'date':
          return (
            <DateFilter
              onFilterSelect={(value) =>
                handleFiltersChange({ ...selectedFilters, [filter.paramName]: [value] })
              }
              label={filter.label}
              width={filter.width}
              minDate={filter.minDate}
              maxDate={filter.maxDate}
              dateFormat={filter.dateFormat}
              roundTo={filter.roundTo}
            />
          )
        case 'autocomplete':
          return (
            <AutocompleteFilter
              label={filter.label}
              options={filter.options}
              width={filter.width}
              listBoxWidth={filter.listBoxWidth}
              selectedOption={filterValue[0]}
              onFilterSelect={(value) => {
                if (value) {
                  handleFiltersChange({ ...selectedFilters, [filter.paramName]: [value] })
                }
              }}
              loadMoreOptions={filter.loadMoreOptions}
            />
          )
        case 'dateRange':
          return (
            <DateRangeFilter
              width={filter.width}
              format={filter.format}
              label={filter.label}
              onFilterSelect={(value) =>
                handleFiltersChange({ ...selectedFilters, [filter.paramName]: [value] })
              }
            />
          )

        default: {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const error: never = filter
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          throw new Error(`Unsupported filter type: ${(filter as any).type}`)
        }
      }
    }

    return (
      <>
        {filters.map((filter) => (
          <Fragment key={filter.paramName}>{getComponent(filter)}</Fragment>
        ))}
      </>
    )
  }, [filters, selectedFilters, handleFiltersChange])

  const selectedFiltersToDisplayInChips = useMemo(() => {
    const freeTextSearchFilters = filters
      .filter((filter) => filter.type === 'freeTextSearch')
      .map((filter) => filter.paramName)
    const selectedFiltersToDisplay = Object.entries(selectedFilters || {}).reduce(
      (acc, [key, value]) => {
        if (!freeTextSearchFilters.includes(key)) {
          acc[key] = value
        }
        return acc
      },
      {} as SelectedFilters
    )

    return selectedFiltersToDisplay
  }, [filters, selectedFilters])

  const handleRemoveFilterValue = useCallback(
    (paramName: string, filter: FilterType) => {
      const { [paramName]: currentFilterValues, ...restSelected } = selectedFilters || {}
      if (!currentFilterValues) return

      const newFilterValues = currentFilterValues.filter(({ value }) => value !== filter.value)

      const newSelectedFilters = {
        ...restSelected,
        ...(newFilterValues?.length === 0 || !newFilterValues
          ? {}
          : { [paramName]: newFilterValues })
      }

      onFiltersChange(newSelectedFilters)
    },
    [selectedFilters, onFiltersChange]
  )

  return (
    <Stack direction="column" sx={{ mx: 2 }}>
      <Stack
        direction="row"
        gap={1}
        rowGap={0}
        sx={{ mb: 0.5, maxWidth: '100%', flexWrap: 'wrap' }}
      >
        {Filters}
      </Stack>

      <SelectedFiltersChips
        onRemoveFilterValue={handleRemoveFilterValue}
        selectedFilters={selectedFiltersToDisplayInChips}
        filters={filters}
      />
    </Stack>
  )
}
